/**
 * Cutover reconciliation (Finance cutover P0, DRY RUNS) — the rehearsal of
 * the certificate that settles Finance against QuickBooks Online, the
 * accounting authority (ruling 2026-10-05). Design:
 * emerge-ops-scratch/finance-cutover-design.md §3–§5.
 *
 * In the order the work happens:
 *   1. freeze eMerge's own Finance population (before the load)
 *   2. load the five QuickBooks exports: each header checked, the control
 *      identity shown to the cent
 *   3. run: the population tie-out and the load-integrity recompute, the
 *      exceptions (on the board kit), the manual match queue
 *   4. build a DRAFT certificate — which cannot be signed in P0
 *
 * The server decides everything, including who may do what; this screen only
 * chooses what to offer. Hand-built forms, no charts. Amounts are cents and
 * printed by integer arithmetic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { BoardTable } from '../../../components/board';
import { financeApi, outcomeOf } from '../financeApi';
import type { ApiResult, ResidentHit } from '../financeApi';
import {
  reconApi, cents, REPORTS, REPORT_LABEL, CLASS_LABEL, CHECK_LABEL, MATCH_REASON,
} from './reconApi';
import type {
  Overview, ImportResult, RunDetail, ExceptionRow, MatchItem, CertificateResult, Identity, Tieout, ReportKind, LineClass,
} from './reconApi';
import './recon.css';

/** Whether the recon view exists for this caller: the routes are registered (the `recon` group is open) and a recon capability is held. */
export function useReconAvailable(enabled: boolean): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    reconApi.overview().then((r) => { if (live) setOk(Boolean(r && r.success && r.data)); }, () => { if (live) setOk(false); });
    return () => { live = false; };
  }, [enabled]);
  return ok;
}

const stamp = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ET' : '—');
const short = (h: string | null | undefined) => (h ? `${h.slice(0, 12)}…` : '—');
const Verdict = ({ ok, yes = 'Holds', no = 'Does not hold' }: { ok: boolean; yes?: string; no?: string }) => (
  <span className={`fr-verdict ${ok ? 'ok' : 'stop'}`}>{ok ? yes : no}</span>
);

function Card({ title, why, children, label }: { title: string; why?: string; children: React.ReactNode; label?: string }) {
  return (
    <section className="um-card fr-card" aria-label={label || title}>
      <div className="um-card-head"><div><h2>{title}</h2>{why && <p className="why">{why}</p>}</div></div>
      <div className="fr-body">{children}</div>
    </section>
  );
}

/** The control identity, to the cent: every figure against the Balance Sheet anchor. */
export function IdentityTable({ identity }: { identity: Identity }) {
  const v = identity.values;
  const d = identity.deltas;
  if (!v) return <p className="um-quiet">No identity was computed.</p>;
  const rows: Array<[string, number | null, number | null | undefined]> = [
    ['Balance Sheet — Accounts Receivable', v.balance_sheet_ar_cents, null],
    ['A/R Aging Detail — total', v.aging_total_cents, d?.aging_minus_bs_cents],
    ['Customer Balance Detail — sum of customers', v.customer_balance_sum_cents, d?.customer_balance_minus_bs_cents],
    ['Open Invoices — invoices − unapplied − credits', v.open_invoices_net_cents, d?.open_invoices_net_minus_bs_cents],
  ];
  return (
    <div>
      <p className="fr-line">Control identity: <Verdict ok={identity.holds} /></p>
      <table className="fr-table" aria-label="Control identity">
        <thead><tr><th scope="col">Figure</th><th scope="col" className="num">Amount</th><th scope="col" className="num">Difference from the Balance Sheet</th></tr></thead>
        <tbody>
          {rows.map(([label, amount, delta]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td className="num">{cents(amount)}</td>
              <td className={`num${delta ? ' off' : ''}`}>{delta === null ? 'anchor' : cents(delta ?? null)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="fr-small">Open Invoices: invoices {cents(v.open_invoices_docs_cents)} · unapplied payments {cents(v.unapplied_cents)} · remaining credits {cents(v.credits_cents)}</p>
      {identity.coverage && (identity.coverage.aging_customers_missing > 0 || identity.coverage.open_invoice_customers_missing > 0) && (
        <p className="um-notice stop">{identity.coverage.aging_customers_missing + identity.coverage.open_invoice_customers_missing} customer(s) with open items are missing from Customer Balance Detail: the exports are not of all customers.</p>
      )}
      {identity.failed && identity.failed.length > 0 && (
        <p className="um-notice stop">Report checks that failed: {identity.failed.map((f) => `${f.report === 'ALL' ? 'across reports' : REPORT_LABEL[f.report as ReportKind] || f.report} — ${CHECK_LABEL[f.check] || f.check}`).join('; ')}.</p>
      )}
    </div>
  );
}

function ReportChecks({ reports }: { reports: { report: ReportKind; ok: boolean; format?: string | null; checks: { check: string; ok: boolean }[] }[] }) {
  return (
    <table className="fr-table" aria-label="Header checks by report">
      <thead><tr><th scope="col">Report</th><th scope="col">Header</th><th scope="col">Failed checks</th></tr></thead>
      <tbody>
        {reports.map((r) => {
          const bad = r.checks.filter((c) => !c.ok);
          return (
            <tr key={r.report}>
              <th scope="row">{REPORT_LABEL[r.report] || r.report}{r.format ? <span className="fr-small"> · {r.format}</span> : null}</th>
              <td><Verdict ok={r.ok && bad.length === 0} yes="Passed" no="Refused" /></td>
              <td>{bad.length ? bad.map((c) => CHECK_LABEL[c.check] || c.check).join('; ') : 'None'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Upload({ onDone }: { onDone: (r: ImportResult) => void }) {
  const [realm, setRealm] = useState('');
  const [asOf, setAsOf] = useState('');
  const [files, setFiles] = useState<Partial<Record<ReportKind, File>>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refused, setRefused] = useState<ImportResult['reports'] | null>(null);
  const ready = realm.trim() && /^\d{4}-\d{2}-\d{2}$/.test(asOf) && REPORTS.every((k) => files[k]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setErr(null); setRefused(null);
    const form = new FormData();
    form.append('realm_id', realm.trim());
    form.append('as_of_date', asOf);
    for (const k of REPORTS) form.append(k, files[k] as File);
    const r = await reconApi.upload(form);
    setBusy(false);
    if (r.success && r.data) { onDone(r.data); return; }
    if (r.code === 'HEADER_REFUSED' && r.detail && Array.isArray((r.detail as any).reports)) setRefused((r.detail as any).reports);
    setErr(outcomeOf(r, { wrote: true }));
  };
  return (
    <form className="um-form fr-upload" onSubmit={submit} aria-label="Load a QuickBooks snapshot">
      <label><span>QuickBooks company (realm) id</span><input value={realm} onChange={(e) => setRealm(e.target.value)} autoComplete="off" required /></label>
      <label><span>As of (the effective date)</span><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} required /></label>
      {REPORTS.map((k) => (
        <label key={k}>
          <span>{REPORT_LABEL[k]}</span>
          <input type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={(e) => setFiles((f) => ({ ...f, [k]: e.target.files && e.target.files[0] ? e.target.files[0] : undefined }))} />
        </label>
      ))}
      <p className="fr-small">Export each report from QuickBooks Online as of the same date, accrual basis, all customers, no filter, as XLSX. The files go to encrypted storage; nothing in them is shown here except checks and totals.</p>
      <div className="btns"><button type="submit" className="um-open" disabled={!ready || busy}>{busy ? 'Checking…' : 'Check and load'}</button></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {refused && <ReportChecks reports={refused} />}
    </form>
  );
}

function ResidentPicker({ onPick }: { onPick: (hit: ResidentHit) => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ResidentHit[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    const r = await financeApi.search(q.trim());
    if (r.success && r.data) { setHits(r.data.items); setErr(null); } else { setHits(null); setErr(outcomeOf(r)); }
  };
  return (
    <div className="fr-picker">
      <form className="um-search" role="search" onSubmit={search}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Resident name" aria-label="Find the resident" />
        <button type="submit" className="um-open">Find</button>
      </form>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {hits && hits.length === 0 && <p className="um-quiet">No resident matches in your scope.</p>}
      {hits && hits.length > 0 && (
        <ul className="fr-hits" aria-label="Residents found">
          {hits.map((h) => <li key={h.id}><button type="button" className="um-open" onClick={() => onPick(h)}>{h.name}{h.facility_name ? ` · ${h.facility_name}` : ''}</button></li>)}
        </ul>
      )}
    </div>
  );
}

function MatchQueue({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<MatchItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<ResidentHit | null>(null);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await reconApi.matches();
    if (r.success && r.data) { setItems(r.data.items); setErr(null); } else { setItems(null); setErr(outcomeOf(r)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (m: MatchItem, kind: 'confirm' | 'reject') => {
    const r = kind === 'confirm'
      ? await reconApi.confirm(m.id, { patient_id: (picked as ResidentHit).id, version: m.version, reason: reason.trim() || undefined })
      : await reconApi.reject(m.id, { reason: reason.trim(), version: m.version });
    if (!r.success) { setMsg(outcomeOf(r, { wrote: true })); return; }
    setMsg(kind === 'confirm' ? 'Matched. The next run will use it.' : 'Rejected. The customer stays unmatched until someone decides again.');
    setOpen(null); setPicked(null); setReason('');
    await load(); onChanged();
  };
  return (
    <Card title="Match queue" why="QuickBooks customers the rule could not match to exactly one resident. A person names the resident; nothing is ever matched on a similar name." label="Match queue">
      {msg && <p className="um-notice" role="status">{msg}</p>}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {!err && items === null && <p className="um-quiet">Loading…</p>}
      {items && items.length === 0 && <p className="um-quiet">Nothing waiting for a person.</p>}
      {items && items.length > 0 && (
        <ul className="fr-queue" aria-label="Customers waiting for a match">
          {items.map((m) => (
            <li key={m.id}>
              <div className="fr-queue-row">
                <span className="who">{m.customer}</span>
                <span className="why">{MATCH_REASON[m.reason || ''] || 'No rule matched this customer.'}</span>
                <button type="button" className="um-open" aria-expanded={open === m.id} onClick={() => { setOpen(open === m.id ? null : m.id); setPicked(null); setReason(''); setMsg(null); }}>{open === m.id ? 'Close' : 'Decide…'}</button>
              </div>
              {open === m.id && (
                <div className="um-form">
                  <ResidentPicker onPick={setPicked} />
                  {picked && <p className="fr-line">Match to <strong>{picked.name}</strong>?</p>}
                  <label><span>Reason (required to reject; optional to match)</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} /></label>
                  <div className="btns">
                    <button type="button" className="um-open" disabled={!picked} onClick={() => act(m, 'confirm')}>Confirm match</button>
                    <button type="button" className="um-open" disabled={reason.trim().length < 10} onClick={() => act(m, 'reject')}>Reject</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function TieoutView({ t }: { t: Tieout | null }) {
  if (!t) return <p className="um-quiet">No population tie-out: the control identity did not hold, so nothing was classified.</p>;
  return (
    <table className="fr-table" aria-label="Population tie-out">
      <tbody>
        <tr><th scope="row">eMerge population (frozen before the load)</th><td className="num">{t.population_members}</td></tr>
        <tr><th scope="row">… backed by a QuickBooks balance</th><td className="num">{t.members_backed_by_qb}</td></tr>
        <tr><th scope="row">… with no QuickBooks balance</th><td className={`num${t.members_without_qb ? ' off' : ''}`}>{t.members_without_qb}</td></tr>
        <tr><th scope="row">QuickBooks customers with a balance</th><td className="num">{t.qb_customers_with_balance} · {cents(t.qb_balance_cents)}</td></tr>
        <tr><th scope="row">… outside eMerge's population</th><td className={`num${t.qb_customers_outside_population ? ' off' : ''}`}>{t.qb_customers_outside_population} · {cents(t.qb_balance_outside_population_cents)}</td></tr>
        <tr><th scope="row">Tie-out</th><td><Verdict ok={t.zero} yes="Zero" no={`${t.delta_count} unexplained`} /></td></tr>
      </tbody>
    </table>
  );
}

function ExceptionDetail({ e, run, laterRuns, me, can, onDone }: { e: ExceptionRow; run: RunDetail; laterRuns: string[]; me: string; can: Overview['can']; onDone: (msg: string) => void }) {
  const [reason, setReason] = useState('');
  const [fix, setFix] = useState('');
  const [proof, setProof] = useState(laterRuns[0] || '');
  const [err, setErr] = useState<string | null>(null);
  if (e.disposition !== 'OPEN') {
    return <p className="fr-line">{e.disposition === 'ACCEPTED' ? `Accepted ${stamp(e.decided_at)} under ${e.threshold_rule}.` : `Resolved ${stamp(e.decided_at)}; proven by a later run.`}{e.reason ? ` Reason: ${e.reason}` : ''}</p>;
  }
  const importer = run.imported_by === me;
  const accept = async () => {
    const r = await reconApi.accept(e.id, { reason: reason.trim(), version: e.version });
    if (r.success) onDone('Accepted.'); else setErr(outcomeOf(r, { wrote: true }));
  };
  const resolve = async () => {
    const r = await reconApi.resolve(e.id, { run_id: proof, version: e.version, reason: fix.trim() });
    if (r.success) onDone('Resolved.'); else setErr(outcomeOf(r, { wrote: true }));
  };
  return (
    <div className="fr-exc">
      {can.accept && (
        <div className="um-form" aria-label="Accept this difference">
          {importer
            ? <p className="um-notice stop">You imported this snapshot, so you cannot accept its exceptions.</p>
            : (
              <>
                <p className="fr-small">Accepting carries the difference as it is. Limit: {cents(run.thresholds.item_cents)} per item, {cents(run.thresholds.aggregate_cents)} per run ({run.thresholds.source === 'DATABASE' ? (run.thresholds.ruling || 'as configured') : 'no limits are configured, so nothing can be accepted'}).</p>
                <label><span>Why this difference is acceptable</span><textarea value={reason} onChange={(ev) => setReason(ev.target.value)} maxLength={2000} /></label>
                <div className="btns"><button type="button" className="um-open" disabled={reason.trim().length < 10 || e.amount_cents === null} onClick={accept}>Accept</button>{e.amount_cents === null && <span className="fr-small">No amount is known, so it cannot be accepted — fix it and resolve.</span>}</div>
              </>
            )}
        </div>
      )}
      {can.resolve && (
        <div className="um-form" aria-label="Resolve this difference">
          <p className="fr-small">Resolving says the data was fixed. A later completed run proves it: over an export as of the same date or later, it no longer raises this difference and shows the invoice, customer or resident as matched. Absent is not fixed.</p>
          {importer
            ? <p className="um-notice stop">You imported this snapshot, so you cannot resolve its exceptions.</p>
            : laterRuns.length === 0
              ? <p className="um-quiet">No later run yet. Fix the data, run again, then resolve.</p>
              : (
                <>
                  <label><span>Proving run</span><select value={proof} onChange={(ev) => setProof(ev.target.value)}>{laterRuns.map((id) => <option key={id} value={id}>{short(id)}</option>)}</select></label>
                  <label><span>What was fixed</span><textarea value={fix} onChange={(ev) => setFix(ev.target.value)} maxLength={2000} /></label>
                  <div className="btns"><button type="button" className="um-open" disabled={!proof || fix.trim().length < 10} onClick={resolve}>Resolve</button></div>
                </>
              )}
        </div>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

function RunView({ runId, overview, onChanged }: { runId: string; overview: Overview; onChanged: () => void }) {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [cert, setCert] = useState<CertificateResult | null>(null);
  const load = useCallback(async () => {
    const r = await reconApi.runDetail(runId);
    if (r.success && r.data) { setRun(r.data); setErr(null); } else { setRun(null); setErr(outcomeOf(r)); }
  }, [runId]);
  useEffect(() => { setCert(null); setMsg(null); load(); }, [load]);
  if (err) return <p className="um-notice stop" role="alert">{err}</p>;
  if (!run) return <p className="um-quiet">Loading the run…</p>;
  const mine = overview.runs.find((r) => r.id === run.id);
  const laterRuns = overview.runs.filter((r) => r.status === 'COMPLETED' && mine && new Date(r.created_at) > new Date(mine.created_at) && r.id !== run.id).map((r) => r.id);
  const byClass = run.counts.by_class || {};
  const build = async () => {
    const r = await reconApi.buildCertificate(run.id);
    if (r.success && r.data) { setCert(r.data); onChanged(); } else setMsg(outcomeOf(r, { wrote: true }));
  };
  return (
    <div className="fr-run">
      <p className="fr-line">Run {short(run.id)} · {stamp(run.created_at)} · {RUN_STATUS_TEXT[run.status] || run.status}</p>
      <div className="fr-grid">
        <div>
          <h3>Population tie-out</h3>
          <TieoutView t={run.population_tieout} />
        </div>
        <div>
          <h3>Load integrity <Verdict ok={run.load_integrity.holds} /></h3>
          <p className="fr-small">Recomputed from the stored rows by a separate query, not from the importer's counts.</p>
          <table className="fr-table" aria-label="Load integrity">
            <thead><tr><th scope="col">Check</th><th scope="col" className="num">Stored</th><th scope="col" className="num">Recomputed</th></tr></thead>
            <tbody>
              {run.load_integrity.checks.map((c) => (
                <tr key={`${c.report}:${c.check}`} className={c.ok ? '' : 'off'}>
                  <th scope="row">{c.check.replace(/_/g, ' ').toLowerCase()}{c.report !== 'ALL' ? ` · ${REPORT_LABEL[c.report as ReportKind] || c.report}` : ''}</th>
                  <td className="num">{c.check === 'DETAIL_ROWS' ? c.stored : cents(c.stored)}</td>
                  <td className="num">{c.check === 'DETAIL_ROWS' ? c.recomputed : cents(c.recomputed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="fr-line">Lines: {(Object.keys(CLASS_LABEL) as LineClass[]).map((k) => `${CLASS_LABEL[k]} ${byClass[k] || 0}`).join(' · ')}</p>
      {(run.counts.matched_total_only || 0) > 0 && (
        <p className="fr-small">{run.counts.matched_total_only} matched on the invoice total only: QuickBooks' open balance differs from the original amount, and eMerge holds no record of the activity between them.</p>
      )}
      {msg && <p className="um-notice" role="status">{msg}</p>}
      <h3>Exceptions</h3>
      {run.exceptions.length === 0
        ? <p className="um-quiet">No exceptions: every line matched.</p>
        : (
          <BoardTable<ExceptionRow>
            label="Exceptions"
            rows={run.exceptions}
            rowKey={(e) => e.id}
            identity={{ label: 'Difference', help: 'What differs, keyed without a name: an invoice number, or a customer token.', render: (e) => <span>{e.doc_number || e.exception_key}</span>, name: (e) => e.doc_number || e.exception_key }}
            columns={[
              { key: 'class', label: 'Kind', help: 'The diff class.', render: (e) => CLASS_LABEL[e.class] },
              { key: 'amount', label: 'At stake', help: 'The cents at stake: the difference, or the one side that has an amount.', numeric: true, render: (e) => cents(e.amount_cents) },
              { key: 'disposition', label: 'Disposition', help: 'OPEN until accepted (with a reason, within the limits) or resolved (a later run proves the fix).', render: (e) => e.disposition.toLowerCase() },
            ]}
            openKey={open}
            onToggle={(k) => setOpen(open === k ? null : k)}
            renderDetail={(e) => <ExceptionDetail e={e} run={run} laterRuns={laterRuns} me={overview.me} can={overview.can} onDone={async (m) => { setMsg(m); setOpen(null); await load(); onChanged(); }} />}
            detailLabel={(e) => `Exception ${e.doc_number || e.exception_key}`}
            sort={null} dir="asc" sorts={[]} onSort={() => undefined}
          />
        )}
      {overview.can.build_certificate && (
        <div className="fr-cert-act"><button type="button" className="um-open" onClick={build}>Build a DRAFT certificate from this run</button></div>
      )}
      {cert && <CertificateView cert={cert} />}
    </div>
  );
}

const RUN_STATUS_TEXT: Record<string, string> = {
  COMPLETED: 'completed',
  IDENTITY_FAILED: 'stopped: the control identity did not hold',
  LOAD_INTEGRITY_FAILED: 'stopped: the stored rows no longer recompute to what was loaded',
};

const PRECONDITION_LABEL: Record<string, string> = {
  run_completed: 'The run completed (identity and load integrity held)',
  zero_open_exceptions: 'No exception is open',
  accepted_within_limits: 'Every accepted exception, and the run\'s accepted total, is within the limits recorded when it was accepted',
  zero_amount_exceptions_reasoned: 'Every zero-amount exception says why it is one',
  control_identity_holds: 'The control identity holds', file_hashes_reverified: 'Every file re-hashes the same',
  load_integrity_holds: 'Load integrity holds', population_tieout_zero_or_accepted: 'Population tie-out is zero, or every member and customer in it is explained by an accepted or resolved exception',
  separation_of_duties_holds: 'Importer, accepters, resolvers and signer are different people',
  run_record_recomputes: 'The run\'s record re-derives from its sealed export, frozen population and recorded matches (its own word is not taken)',
  monday_ingest_absent_since_cutoff: 'Monday Finance ingest absent since the cutoff (P1)',
};

export function CertificateView({ cert }: { cert: CertificateResult }) {
  const s = cert.summary;
  return (
    <section className="fr-cert" aria-label="Draft certificate">
      <h3>DRAFT certificate {cert.replayed ? '(unchanged — the same certificate)' : ''}</h3>
      <p className="um-notice stop">A DRAFT. It cannot be signed in P0: signing is P1, by the Finance lead alone, with re-authentication.</p>
      <p className="fr-line">payload SHA-256 <code>{cert.payload_sha256}</code></p>
      <p className="fr-line">As of {s.as_of_date} · effective {stamp(s.effective_at)}</p>
      <p className="fr-line">Customers matched: by rule {s.customers_matched_by_method.DETERMINISTIC} · by a person {s.customers_matched_by_method.MANUAL} · undecided {s.customers_matched_by_method.PROPOSED_UNDECIDED}</p>
      <p className="fr-line">Exceptions: open {s.exceptions.open} · accepted {s.exceptions.accepted} ({cents(s.exceptions.accepted_total_cents)}) · resolved {s.exceptions.resolved}</p>
      {s.exceptions.zero_amount && (
        <p className="fr-line">Zero-amount exceptions (counted apart; they use none of the limits): {s.exceptions.zero_amount.count} · open {s.exceptions.zero_amount.open} · accepted {s.exceptions.zero_amount.accepted} · resolved {s.exceptions.zero_amount.resolved}</p>
      )}
      {s.manual_matches && s.manual_matches.length > 0 && (
        <p className="fr-line">Matched by a person, one by one: {s.manual_matches.map((m) => `${m.customer_token} → ${short(m.patient_id || m.facility_id || '')}`).join(' · ')}</p>
      )}
      {s.population_tieout_explained && !s.population_tieout_explained.holds && s.population_tieout_explained.unexplained_members !== null && (
        <p className="fr-line">Population tie-out unexplained: {s.population_tieout_explained.unexplained_members} member(s) without QuickBooks · {s.population_tieout_explained.unexplained_customers} QuickBooks customer(s) outside the population</p>
      )}
      {s.lines && s.lines.matched_total_only > 0 && (
        <p className="fr-line">Matched on the invoice total only (not the open balance): {s.lines.matched_total_only}</p>
      )}
      <ul className="fr-pre" aria-label="What signing would require">
        {Object.entries(s.sign_preconditions).map(([k, v]) => <li key={k}><Verdict ok={v === true} yes="Yes" no={v === null ? 'Not yet measurable' : 'No'} /> {PRECONDITION_LABEL[k] || k}</li>)}
      </ul>
      <p className="fr-small">Files: {s.files.map((f) => `${REPORT_LABEL[f.report as ReportKind] || f.report} ${f.verified ? 're-verified' : `NOT verified (${(f.why || '').toLowerCase().replace(/_/g, ' ')})`}`).join(' · ')}</p>
    </section>
  );
}

export default function CutoverReconciliation() {
  const [ov, setOv] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [imported, setImported] = useState<ImportResult | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r: ApiResult<Overview> = await reconApi.overview();
    if (r.success && r.data) { setOv(r.data); setErr(null); return; }
    setOv(null);
    setErr(r.status === 404 ? 'Cutover reconciliation is not open in this environment.' : outcomeOf(r));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <section className="um-card"><p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err}</p></section>;
  if (!ov) return <section className="um-card"><p className="um-quiet" style={{ padding: 12 }}>Opening the reconciliation…</p></section>;

  const freeze = async () => {
    const r = await reconApi.freezePopulation();
    setMsg(r.success ? 'Population frozen. Load the QuickBooks snapshot after this, never before.' : outcomeOf(r, { wrote: true }));
    await load();
  };
  const run = async (snapshotId: string) => {
    const r = await reconApi.run(snapshotId);
    if (r.success && r.data) { setRunId(r.data.run_id); setMsg(null); } else setMsg(outcomeOf(r, { wrote: true }));
    await load();
  };

  return (
    <div className="fr">
      <section className="um-card fr-card" aria-label="About this rehearsal">
        <div className="um-card-head"><div>
          <h2>Cutover reconciliation — rehearsal</h2>
          <p className="why">QuickBooks Online is the accounting authority; Monday is history and appears nowhere below. This reconciles eMerge against QuickBooks exports and builds a DRAFT certificate. Nothing here changes a balance, and nothing here can be signed.</p>
        </div></div>
        {!ov.storage_configured && <p className="um-notice stop" style={{ margin: 12 }}>No encrypted storage is configured for QuickBooks exports here, so a snapshot cannot be loaded.</p>}
        {msg && <p className="um-notice" role="status" style={{ margin: 12 }}>{msg}</p>}
      </section>

      <Card title="1 · Freeze eMerge's population" why="Residents eMerge says Finance is collecting from — active handoffs and sales past handoff — frozen before the QuickBooks load, so the comparison is not circular.">
        {ov.can.freeze_population && <div className="btns"><button type="button" className="um-open" onClick={freeze}>Freeze the population now</button></div>}
        {ov.populations.length === 0 ? <p className="um-quiet">No population has been frozen.</p> : (
          <ul className="fr-list">{ov.populations.map((p) => <li key={p.id}>{stamp(p.frozen_at)} · {p.member_count} residents · {p.rule_version}</li>)}</ul>
        )}
      </Card>

      {ov.can.import && (
        <Card title="2 · Load a QuickBooks snapshot" why="The five reports, as of one date. Each header is checked; any failure refuses the whole snapshot and stores nothing. An identical upload is recognised and stores nothing new.">
          <Upload onDone={(r) => { setImported(r); load(); }} />
          {imported && (
            <div className="fr-result" aria-label="Snapshot loaded">
              <p className="um-notice" role="status">{imported.replayed ? 'This exact snapshot was already loaded; nothing new was stored.' : `Loaded and sealed: ${imported.counts.rows} rows from ${imported.counts.files} files.`}</p>
              <ReportChecks reports={imported.reports} />
              <IdentityTable identity={imported.identity} />
            </div>
          )}
        </Card>
      )}

      <Card title="3 · Snapshots and runs" why="A run compares a sealed snapshot with the latest population frozen before it was loaded.">
        {ov.snapshots.length === 0 ? <p className="um-quiet">No snapshot has been loaded.</p> : (
          <ul className="fr-list" aria-label="Snapshots">
            {ov.snapshots.map((s) => (
              <li key={s.id}>
                <span>As of {s.as_of_date} · company {s.realm_id} · loaded {stamp(s.imported_at)} · identity <Verdict ok={Boolean(s.identity && s.identity.holds)} /></span>
                {ov.can.run && <button type="button" className="um-open" onClick={() => run(s.id)}>Run</button>}
              </li>
            ))}
          </ul>
        )}
        {ov.runs.length > 0 && (
          <ul className="fr-list" aria-label="Runs">
            {ov.runs.map((r) => (
              <li key={r.id}>
                <span>Run {short(r.id)} · {stamp(r.created_at)} · {r.status === 'COMPLETED' ? `${r.counts.exceptions ?? 0} exceptions` : (r.status === 'LOAD_INTEGRITY_FAILED' ? 'load integrity failed' : 'identity failed')}</span>
                <button type="button" className="um-open" aria-pressed={runId === r.id} onClick={() => setRunId(runId === r.id ? null : r.id)}>{runId === r.id ? 'Hide' : 'Open'}</button>
              </li>
            ))}
          </ul>
        )}
        {runId && <RunView runId={runId} overview={ov} onChanged={load} />}
      </Card>

      {ov.can.match && <MatchQueue onChanged={load} />}
    </div>
  );
}
